import { createFileRoute, Link } from '@tanstack/react-router';
import { CheckCircle2, FileDiff } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { useContractChanges } from '@/hooks/useContractChanges';
import { formatCurrency, formatDate } from '@/lib/format';
import type {
  ContractChangeCustomer,
  ContractChangeItem,
  UnmappedClientSummary,
} from '@/types/contractReconciliation';

/**
 * Kundenübergreifende Liste offener Vertragsänderungen.
 *
 * Der Abgleich selbst (Verknüpfen, Abhaken) lebt im Kunden-Tab; hier
 * nur die Übersicht plus Tiefenlinks, damit Fälle wie WKR 0118 nicht
 * unbemerkt bleiben.
 */

export const Route = createFileRoute('/contracts/changes')({
  component: ContractChangesPage,
});

function ContractChangesPage(): JSX.Element {
  const { data, isLoading, isError, error } = useContractChanges();

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold">Vertragsänderungen</h1>
        <p className="mt-1 max-w-3xl text-muted">
          Was seit dem letzten Abgleich in den wiederkehrenden Rechnungen dazugekommen
          oder weggefallen ist, und welche Verträge noch keinem Kunden gehören.
        </p>
      </header>

      {isLoading ? <SkeletonRows rows={4} cols={4} /> : null}
      {isError ? <ErrorAlert error={error} /> : null}

      {data ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KpiTile testId="kpi-open-total" label="Offen gesamt" value={data.open_total} />
            <KpiTile testId="kpi-new" label="Neue Positionen" value={data.positions_new} />
            <KpiTile testId="kpi-removed" label="Weggefallen" value={data.positions_removed} />
            <KpiTile
              testId="kpi-unmapped"
              label="Verträge ohne Kunde"
              value={data.unmapped_contracts}
            />
          </div>

          {data.customers.length === 0 && data.unmapped_clients.length === 0 ? (
            <EmptyState
              icon={<CheckCircle2 className="size-8" />}
              title="Nichts offen"
              description="Alle Vertragsänderungen sind zugeordnet oder abgehakt."
            />
          ) : null}

          {data.customers.map((customer) => (
            <CustomerChangeCard key={customer.customer_id} customer={customer} />
          ))}

          {data.unmapped_clients.length > 0 ? (
            <UnmappedClientsCard clients={data.unmapped_clients} />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function KpiTile({
  testId,
  label,
  value,
}: {
  testId: string;
  label: string;
  value: number;
}): JSX.Element {
  return (
    <div
      data-testid={testId}
      className="rounded-lg border border-border bg-surface px-4 py-3"
    >
      <p className="text-sm text-muted">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function CustomerChangeCard({
  customer,
}: {
  customer: ContractChangeCustomer;
}): JSX.Element {
  const customerLink = {
    to: '/customers/$id' as const,
    params: { id: customer.customer_id },
    search: { tab: 'reconciliation' as const },
  };

  return (
    <Card
      title={
        <Link
          to={customerLink.to}
          params={customerLink.params}
          search={customerLink.search}
          className="text-primary hover:underline"
        >
          {customer.customer_name}
        </Link>
      }
      description={`${customer.positions_new} neu, ${customer.positions_removed} weggefallen`}
      actions={
        <Link to={customerLink.to} params={customerLink.params} search={customerLink.search}>
          <Button size="sm" variant="secondary" leftIcon={<FileDiff className="size-4" />}>
            Abgleich öffnen
          </Button>
        </Link>
      }
    >
      <DataTable
        caption={`Offene Vertragsänderungen für ${customer.customer_name}`}
        columns={CHANGE_COLUMNS}
        rows={customer.items}
        rowKey={(item) => item.service_profile_id}
        storageKey="vertragsaenderungen"
      />
    </Card>
  );
}

function ohneStrich(text: string): string {
  return text === '–' ? '' : text;
}

function changeDatePart(item: ContractChangeItem): string | null {
  return item.changed_at.split('T')[0] ?? null;
}

const CHANGE_COLUMNS: ReadonlyArray<Column<ContractChangeItem>> = [
  {
    id: 'aenderung',
    header: 'Änderung',
    filter: 'select',
    filterValue: (item) => (item.kind === 'neu' ? 'neu' : 'weggefallen'),
    cell: (item) =>
      item.kind === 'neu' ? (
        <Badge tone="warning" data-testid={`change-${item.kind}`}>
          neu
        </Badge>
      ) : (
        <Badge tone="danger" data-testid={`change-${item.kind}`}>
          weggefallen
        </Badge>
      ),
  },
  {
    id: 'position',
    header: 'Position',
    filterValue: (item) =>
      [item.product_key ?? '(ohne Produktschlüssel)', item.notes ?? ''].filter(Boolean).join(' '),
    cell: (item) => (
      <>
        <div>{item.product_key ?? '(ohne Produktschlüssel)'}</div>
        {item.notes ? <div className="text-xs text-muted">{item.notes}</div> : null}
      </>
    ),
  },
  {
    id: 'kategorie',
    header: 'Kategorie',
    filter: 'select',
    filterValue: (item) => item.service_category,
    cell: (item) => (
      <>
        {item.service_category}
        {item.contract_number ? (
          <span className="text-muted"> · {item.contract_number}</span>
        ) : null}
      </>
    ),
  },
  {
    id: 'betrag',
    header: 'Betrag',
    filterValue: (item) => ohneStrich(formatCurrency(item.line_total)),
    cell: (item) => formatCurrency(item.line_total),
  },
  {
    id: 'seit',
    header: 'Seit',
    filterValue: (item) => ohneStrich(formatDate(changeDatePart(item))),
    cell: (item) => formatDate(changeDatePart(item)),
  },
  {
    id: 'systeme',
    header: 'Systeme',
    filterValue: (item) => item.linked_system_names.join(', '),
    cell: (item) =>
      item.linked_system_names.length > 0 ? item.linked_system_names.join(', ') : '–',
  },
];

function UnmappedClientsCard({
  clients,
}: {
  clients: UnmappedClientSummary[];
}): JSX.Element {
  return (
    <Card
      title="Verträge ohne Kunden"
      description="Solange ein Vertrag keinem Kunden gehört, merkt niemand, wenn er sich ändert."
      actions={
        <Link
          to="/settings/contracts/mapping"
          className="text-sm text-primary hover:underline"
        >
          Im Zuordnungs-Assistenten öffnen
        </Link>
      }
    >
      <ul className="flex flex-col gap-2 text-sm">
        {clients.map((client) => (
          <li key={client.invoice_ninja_client_id}>
            {client.client_name ?? client.invoice_ninja_client_id} · {client.contracts_count}{' '}
            Verträge
          </li>
        ))}
      </ul>
    </Card>
  );
}
